// web tapioca
// app/components/chatbotReply.ts
//
// The adapter seam for the chat widget: it posts the transcript to
// /api/chat (Qwen + pgvector article search) and hands the answer back.
//
// It still takes the caller's bound `t` because two strings never leave the
// client — the opening greeting (no request is made for it) and the
// rate-limit notice — and both must stay in messages/*.json per CLAUDE.md.

// Sentinel on the thrown Error's message — the widget checks for it and reopens
// the consent gate. One string beats an error class for one call site.
export const CONSENT_REQUIRED = "CONSENT_REQUIRED";

export type ChatSource = { title: string; url: string };
export type ChatMsg = { role: "user" | "bot"; text: string; sources?: ChatSource[] };
export type ChatReply = { text: string; chips?: string[]; sources?: ChatSource[] };

// The sources come from the route, which built them from model-steered tool
// calls, and they end up in an `<a href>`. Same story for the copy restored
// from localStorage, which is why this is exported. Anything that is not an
// http(s) link with a title is dropped — a `javascript:` href never gets there.
export function safeSources(value: unknown): ChatSource[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ok = value.filter(
    (s: unknown): s is ChatSource =>
      !!s &&
      typeof s === "object" &&
      typeof (s as ChatSource).title === "string" &&
      (s as ChatSource).title.trim() !== "" &&
      typeof (s as ChatSource).url === "string" &&
      // Host required, not just the scheme: "https://" passes a bare scheme
      // check but throws in `new URL`, which the history rebuild below calls.
      /^https?:\/\/[^/\s]/.test((s as ChatSource).url),
  );
  return ok.length ? ok : undefined;
}

// The first four render as the greeting chips, so they must be answerable from
// the site copy (the system prompt forbids MOQ/price answers).
const CHIP_KEYS = [
  "products",
  "packing",
  "shipping",
  "quality",
  "moq",
  "samples",
] as const;

// /api/chat rejects a body outside these limits with 400 (see the zod schema
// in app/api/chat/route.ts). The widget keeps 30 messages in localStorage and
// a bot answer can run long, so trim rather than let the route refuse.
const MAX_HISTORY = 20;
const MAX_TEXT = 2000;
const TIMEOUT_MS = 30_000;

export async function getChatReply(
  history: ChatMsg[],
  t: (key: string) => string,
  locale: string,
): Promise<ChatReply> {
  // Opening the panel with an empty transcript: greet locally. No token spend,
  // no latency, and the copy stays translated in messages/*.json.
  if (!history.some((m) => m.role === "user")) {
    return {
      text: t("greeting"),
      chips: CHIP_KEYS.slice(0, 4).map((k) => t(`chip.${k}`)),
    };
  }

  // The route requires the last message to be a user turn. After an error the
  // transcript ends with the error bubble, so the retry chip would 400 without
  // this cut. The widget-local error bubbles are then dropped so they never
  // reach the model as its own prior answers.
  const errorText = t("error");
  const rateLimitedText = t("rateLimited");
  const messages = history
    .slice(0, history.findLastIndex((m) => m.role === "user") + 1)
    .filter(
      (m) =>
        m.role !== "bot" ||
        (m.text !== errorText && m.text !== rateLimitedText),
    )
    .slice(-MAX_HISTORY)
    .map((m) => ({
      role: m.role,
      // The route strips the model's SOURCES line before we ever see it. Put it
      // back on the way out, or from turn 2 every assistant example in the
      // model's own history contradicts the rule that answers end with one.
      text: (m.sources?.length
        ? `${m.text}\nSOURCES: ${m.sources.map((s) => new URL(s.url).pathname).join(", ")}`
        : m.text
      ).slice(0, MAX_TEXT),
    }));

  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ locale, messages }),
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });

  // The gate's cookie is gone or was never set. Thrown, not returned as text:
  // the widget reopens the gate instead of rendering a bubble.
  if (res.status === 403) throw new Error(CONSENT_REQUIRED);

  if (res.status === 429) {
    return { text: t("rateLimited"), chips: [t("retry")] };
  }

  // 400 / 502 / 503 and network failures all mean the same thing to a visitor;
  // the widget's own catch renders t("error") and offers a retry.
  if (!res.ok) throw new Error(`chat ${res.status}`);

  const { text, sources } = (await res.json()) as {
    text: string;
    sources?: unknown;
  };
  return { text, sources: safeSources(sources) };
}
